import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Room } from "../types";
import useSWR from "swr";
import { fetcher, apiRequest } from "../utils/api";

function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [isPressed, setIsPressed] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emergencyTriggered, setEmergencyTriggered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: room, error: roomError } = useSWR<Room>(
    roomId ? `/rooms/${roomId}` : null,
    fetcher
  );

  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  // Initialize audio context on first user interaction
  const initializeAudio = useCallback(() => {
    if (!audioContext) {
      try {
        const ctx = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        setAudioContext(ctx);

        // Resume audio context if it's suspended (iOS requirement)
        if (ctx.state === "suspended") {
          ctx.resume();
        }
      } catch (error) {
        console.warn("Audio context not supported:", error);
      }
    }
  }, [audioContext]);

  const playSound = useCallback(
    (type: "start" | "tick" | "complete") => {
      if (!audioContext) return;

      try {
        // Resume audio context if suspended
        if (audioContext.state === "suspended") {
          audioContext.resume();
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        if (type === "start") {
          oscillator.frequency.value = 800;
          gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.1
          );
        } else if (type === "tick") {
          oscillator.frequency.value = 1000;
          gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.1
          );
        } else if (type === "complete") {
          oscillator.frequency.value = 1200;
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.5
          );
        }

        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.5);
      } catch (error) {
        console.warn("Error playing sound:", error);
      }
    },
    [audioContext]
  );

  const triggerHaptics = useCallback((type: "start" | "tick" | "complete") => {
    try {
      // Check if vibration is supported and available
      if ("vibrate" in navigator && typeof navigator.vibrate === "function") {
        if (type === "start") {
          navigator.vibrate(100);
        } else if (type === "tick") {
          navigator.vibrate(50);
        } else if (type === "complete") {
          navigator.vibrate([200, 100, 200, 100, 200]);
        }
      }

      // Fallback for older Android devices
      else if ("mozVibrate" in navigator) {
        const mozVibrate = (navigator as any).mozVibrate;
        if (typeof mozVibrate === "function") {
          if (type === "start") {
            mozVibrate(100);
          } else if (type === "tick") {
            mozVibrate(50);
          } else if (type === "complete") {
            mozVibrate([200, 100, 200, 100, 200]);
          }
        }
      }
    } catch (error) {
      console.warn("Vibration not supported:", error);
    }
  }, []);

  const handlePressStart = useCallback(() => {
    if (emergencyTriggered || loading) return;

    // Initialize audio context on first user interaction
    initializeAudio();

    setIsPressed(true);
    setCountdown(3);
    setError(null);

    // Play sound and haptics on start
    playSound("start");
    triggerHaptics("start");
  }, [emergencyTriggered, loading, initializeAudio, playSound, triggerHaptics]);

  const handlePressEnd = useCallback(() => {
    setIsPressed(false);
    setCountdown(0);
  }, []);

  const triggerEmergency = useCallback(async () => {
    if (!roomId || loading) return;

    setLoading(true);
    setError(null);

    try {
      await apiRequest("/emergencies", {
        method: "POST",
        body: JSON.stringify({ room_id: roomId }),
      });

      setEmergencyTriggered(true);

      // Play completion sound and haptics
      playSound("complete");
      triggerHaptics("complete");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to trigger emergency"
      );
    } finally {
      setLoading(false);
    }
  }, [roomId, loading, playSound, triggerHaptics]);

  useEffect(() => {
    if (!isPressed || countdown === 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        triggerEmergency();
        setIsPressed(false);
      } else {
        setCountdown(countdown - 1);
        // Play tick sound and haptics for each countdown
        playSound("tick");
        triggerHaptics("tick");
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isPressed, countdown, triggerEmergency, playSound, triggerHaptics]);

  if (roomError) {
    return (
      <div className="container">
        <div
          className="card"
          style={{ textAlign: "center", marginTop: "4rem" }}
        >
          <h1>❌ Room Not Found</h1>
          <p>The requested room could not be found.</p>
          <Link to="/" className="btn btn-primary">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="container">
        <div
          className="card"
          style={{ textAlign: "center", marginTop: "4rem" }}
        >
          <h1>Loading...</h1>
          <div style={{ marginTop: "2rem" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid var(--border-color)",
                borderTop: "4px solid var(--primary-color)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto",
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  if (emergencyTriggered) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: "3rem", marginBottom: "2rem" }}>
            🚨 Help is on the way!
          </h1>
          <div className="alert alert-success">
            <p>Emergency alert has been sent to hospital staff.</p>
            <p>Please stay calm and wait for assistance.</p>
          </div>
          <div
            className="card"
            style={{ marginTop: "2rem", textAlign: "left" }}
          >
            <h3>📍 Room Information:</h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
              }}
            >
              <div>
                <strong>Hospital:</strong>
                <br />
                {room.hospital_name}
              </div>
              <div>
                <strong>Room:</strong>
                <br />
                {room.label}
              </div>
              <div>
                <strong>Floor:</strong>
                <br />
                {room.floor}
              </div>
              <div>
                <strong>Address:</strong>
                <br />
                {room.hospital_address}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>
          🏥 {room.hospital_name}
        </h1>
        <h2 style={{ color: "var(--text-muted)", marginBottom: "3rem" }}>
          Room {room.label} - Floor {room.floor}
        </h2>

        {error && <div className="alert alert-error">{error}</div>}

        <p style={{ fontSize: "1.2rem", marginBottom: "2rem" }}>
          Press and hold the emergency button for 3 seconds to call for help
        </p>

        <button
          className={`emergency-button ${isPressed ? "pressing" : ""}`}
          onMouseDown={handlePressStart}
          onMouseUp={handlePressEnd}
          onMouseLeave={handlePressEnd}
          onTouchStart={handlePressStart}
          onTouchEnd={handlePressEnd}
          onTouchCancel={handlePressEnd}
          disabled={loading}
        >
          {loading ? "SENDING..." : "EMERGENCY"}
          {isPressed && countdown > 0 && (
            <div className="countdown-overlay">{countdown}</div>
          )}
        </button>

        <p
          style={{
            fontSize: "1rem",
            color: "var(--text-muted)",
            marginTop: "1rem",
          }}
        >
          Hold button for {isPressed ? countdown : 3} seconds to activate
        </p>
      </div>
    </div>
  );
}

export default RoomPage;
